import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import { StreamHttpError, StreamingHelperService } from "../streaming-helper-service";
import { ICollabStreamEvent } from "./collab-types";

// One SSE stream per open collab session (screen tab). Fetch-based (NOT native EventSource,
// which can't send the designer's Bearer auth header), with the board stream's fixed-delay
// reconnect loop. Presence is connection-derived on the server: keeping this stream open IS
// being present, and the reconnect loop normally beats the server's 30s grace window after a
// transient drop.

const RECONNECT_DELAY_MS: number = 3000;

export interface ICollabStreamHandle {
  stop: () => void;
}

function isCollabStreamEvent(data: unknown): data is ICollabStreamEvent {
  return typeof data === "object" && data !== null && typeof (data as { type?: unknown }).type === "string";
}

export function connectCollabEventStream(
  entityId: string,
  clientId: string,
  onEvent: (streamEvent: ICollabStreamEvent) => void,
  onReconnect: () => void,
  entityRoute: string = "screen",
  /**
   * The stream was refused with an HTTP status. Reported raw so the session owner can
   * apply its own policy — a 404/403 here means the server no longer has this session
   * (or our membership in it), which reconnecting alone can never fix: the loop would
   * re-hit the same 404 every 3s forever while the tab silently discarded every edit
   * (KOA-18256).
   */
  onHttpError?: (status: number) => void
): ICollabStreamHandle {
  let running: boolean = true;
  let abortController: AbortController | null = null;

  const run = async (): Promise<void> => {
    const url: string = `${CommonHelperFunctions.GetBackendUrl()}/collab/${entityRoute}/${entityId}/stream?clientId=${encodeURIComponent(clientId)}`;
    let firstAttempt: boolean = true;
    while (running) {
      if (!firstAttempt) {
        // A gap may have opened while we were offline — the provider refetches state.
        onReconnect();
      }
      firstAttempt = false;
      const controller: AbortController = new AbortController();
      abortController = controller;
      try {
        await StreamingHelperService.streamEvents(
          url,
          (data: unknown) => {
            if (isCollabStreamEvent(data)) onEvent(data);
          },
          controller.signal
        );
      } catch (error: unknown) {
        // network/stream error — fall through to the reconnect delay
        if (error instanceof StreamHttpError) {
          onHttpError?.(error.status);
        }
      }
      if (!running) break;
      await new Promise<void>((resolve: () => void) => setTimeout(resolve, RECONNECT_DELAY_MS));
    }
  };

  void run();

  return {
    stop: (): void => {
      running = false;
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    }
  };
}
